import React, { useEffect, useMemo, useRef, useState } from "react";

const titleMap = {
  1: "intro",
  2: "intro",
  3: "event",
};

const useOptionMap = {
  1: "4to7",
  2: "4to7",
  3: "1to1",
  4: "1to1",
  5: "4to7",
  6: "1to1",
  7: "1to1",
  "문자메시지": "4to7",
  "": "4to7",
};

const previewSizeMap = {
  intro: {
    "1to1": { width: 431, height: 431 },
    "4to7": { width: 431, height: Math.round((431 * 1792) / 1024) },
  },
  event: {
    "1to1": { width: 365, height: 365 },
    "4to7": { width: 365, height: Math.round((365 * 1792) / 1024) },
  },
};

function parseNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function ensureDataUrl(b64OrDataUrl) {
  if (!b64OrDataUrl) return "";
  if (String(b64OrDataUrl).startsWith("data:")) return String(b64OrDataUrl);
  return `data:image/png;base64,${b64OrDataUrl}`;
}

async function preloadTemplateFonts(rootEl) {
  if (!rootEl || !document.fonts?.load) {
    try {
      if (document.fonts?.ready) await document.fonts.ready;
    } catch {}
    return;
  }

  const textElements = [rootEl, ...Array.from(rootEl.querySelectorAll("*"))].filter((el) => {
    try {
      return Boolean((el.textContent || "").trim());
    } catch {
      return false;
    }
  });

  const fontSpecs = new Map();

  for (const el of textElements) {
    try {
      const style = window.getComputedStyle(el);
      const fontStyle = style.fontStyle || "normal";
      const fontWeight = style.fontWeight || "400";
      const fontSize = style.fontSize || "16px";
      const fontFamily = style.fontFamily || "sans-serif";
      const fontSpec = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
      const sampleText = (el.textContent || "").trim().slice(0, 64) || "가나다ABC123";
      fontSpecs.set(fontSpec, sampleText);
    } catch {}
  }

  try {
    await Promise.all(
      Array.from(fontSpecs.entries()).map(([fontSpec, sampleText]) =>
        document.fonts.load(fontSpec, sampleText)
      )
    );
  } catch {}

  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {}
}

export default function InternalTemplateCapturePage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const title = useMemo(() => parseNumber(params.get("title")), [params]);
  const channel = useMemo(() => parseNumber(params.get("channel")), [params]);
  const useOptionOverride = useMemo(() => {
    const raw = String(params.get("use_option") || "").trim();
    return raw || null;
  }, [params]);
  const templateIdsOverride = useMemo(() => {
    const raw = String(params.get("template_ids") || "").trim();
    if (!raw) return null;

    const ids = raw
      .split(",")
      .map((value) => Number(String(value).trim()))
      .filter((value) => Number.isFinite(value) && value > 0);

    return ids.length ? ids : null;
  }, [params]);

  const [captureInput, setCaptureInput] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [finalImageDataUrl, setFinalImageDataUrl] = useState(null);

  const didChooseRef = useRef(false);

  const folderTitle = titleMap[title] || "intro";
  const folderUseOption = useOptionOverride || useOptionMap[channel] || "4to7";
  const previewSize =
    previewSizeMap[folderTitle]?.[folderUseOption] ||
    (folderUseOption === "4to7"
      ? { width: 431, height: Math.round((431 * 1792) / 1024) }
      : { width: 431, height: 431 });
  const captureWidth = previewSize.width;
  const captureHeight = previewSize.height;

  useEffect(() => {
    window.__CAPTURE_READY__ = false;
    window.__CAPTURE_RESULT__ = null;
    window.__setCaptureData = (data) => {
      setCaptureInput({
        originImageDataUrl: ensureDataUrl(data?.originImageBase64 || data?.originImageDataUrl),
        copyright: data?.copyright || "",
        storeName: data?.storeName || "",
        roadName: data?.roadName || "",
      });
    };

    return () => {
      try {
        delete window.__setCaptureData;
        delete window.__CAPTURE_RESULT__;
      } catch {}
    };
  }, []);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templateModules = [];
        const candidateIds = templateIdsOverride || Array.from({ length: 10 }, (_, index) => index + 1);
        for (const i of candidateIds) {
          if (folderTitle === "intro" && folderUseOption === "4to7" && (i === 1 || i === 2 || i === 3 || i === 4)) continue;
          if (folderTitle === "intro" && folderUseOption === "1to1" && i !== 3) continue;
          if (folderTitle === "event" && folderUseOption === "1to1" && i !== 2 && i !== 3) continue;
          if (folderTitle === "event" && folderUseOption === "4to7" && (i === 1 || i === 2 || i === 5)) continue;

          try {
            const module = await import(
              `../Ads/template/${folderTitle}/${folderUseOption}/Template${i}.jsx`
            );
            templateModules.push({ id: i, Component: module.default });
          } catch (error) {
            if (String(error?.message || "").includes("Cannot find module")) {
              continue;
            }
            throw error;
          }
        }
        setTemplates(templateModules);
      } catch (e) {
        console.error("[InternalTemplateCapturePage] loadTemplates error:", e);
        setTemplates([]);
      }
    };

    loadTemplates();
  }, [folderTitle, folderUseOption, templateIdsOverride]);

  useEffect(() => {
    if (didChooseRef.current) return;
    if (!templates || templates.length === 0) return;

    const randomIndex = Math.floor(Math.random() * templates.length);
    setChosen(templates[randomIndex]);
    didChooseRef.current = true;
  }, [templates]);

  useEffect(() => {
    didChooseRef.current = false;
    setChosen(null);
    setFinalImageDataUrl(null);
  }, [folderTitle, folderUseOption]);

  useEffect(() => {
    let canceled = false;

    const runExport = async () => {
      if (!captureInput) return;
      if (!chosen) return;

      const key = `template_${folderTitle}_${folderUseOption}_${chosen.id}`;

      const waitFrame = () =>
        new Promise((r) => {
          requestAnimationFrame(() => requestAnimationFrame(r));
        });

      try {
        const captureRoot = document.getElementById("capture-root");
        await preloadTemplateFonts(captureRoot);
        await waitFrame();

        const exporter = window.__templateExports?.[key];
        if (typeof exporter !== "function") {
          throw new Error(`exporter not found for key=${key}`);
        }

        const dataUrl = await exporter();
        if (canceled) return;

        setFinalImageDataUrl(dataUrl);
        window.__CAPTURE_RESULT__ = dataUrl;
        await waitFrame();

        window.__CAPTURE_READY__ = true;
      } catch (e) {
        console.error("[InternalTemplateCapturePage] export error:", e);
        window.__CAPTURE_RESULT__ = null;
        window.__CAPTURE_READY__ = false;
      }
    };

    runExport();
    return () => {
      canceled = true;
    };
  }, [captureInput, chosen, folderTitle, folderUseOption]);

  const TemplateComponent = chosen?.Component;

  return (
    <div style={{ width: 1100, padding: 10, background: "#fff" }}>
      <div
        id="capture-root"
        className="internal-template-capture"
        style={{ width: captureWidth, height: captureHeight }}
      >
        {finalImageDataUrl ? (
          <img
            src={finalImageDataUrl}
            alt="capture"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : TemplateComponent && captureInput ? (
          <TemplateComponent
            imageUrl={captureInput.originImageDataUrl}
            text={captureInput.copyright}
            storeName={captureInput.storeName}
            roadName={captureInput.roadName}
            isCaptured
          />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            loading
          </div>
        )}
      </div>
    </div>
  );
}
